import { bound } from "@frui.ts/helpers";
import { Router } from "@frui.ts/screens/dist";
import { attachManualValidator, getValidationMessage } from "@frui.ts/validation";
import { attachAutomaticBlurValidator } from "utils/validationPatch";
import { plainToClass } from "class-transformer";
import { action, computed, observable, reaction } from "mobx";
import NiaResponse from "models/niaResponse";
import ChargeRegistrationService from "services/chargeRegistrationService";
import EnumService from "services/enumService";
import EventBus, {
  refundRequestCreationFailed,
  refundRequestDocumentsUploadFailed,
  refundRequestDocumentsUploadSuccess,
  refundRequestISDSConceptStatusCheckFailure,
  refundRequestISDSConceptStatusCheckSuccess,
  refundRequestISDSSessionIdHandled,
  refundRequestNIAResponseHandled,
} from "services/eventBus";
import LocalizationService from "services/localizationService";
import NiaService, { Notice } from "services/NIA/niaService";
import { SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY } from "services/omissionService";
import AppRouter from "services/appRouter";
import VehicleInfoViewModel from "viewModels/common/vehicleInfoViewModel";
import { getTooltipPosition } from "viewModels/utils";
import ISDSService from "../../../services/ISDS/isdsService";
import serializeNotice from "../../../utils/serializeNotice";
import NoticeViewModel from "../noticeViewModel";
import { PersistedBase64, SignatureType } from "../../../models/notice";
import ApplicantPersonViewModel from "../components/applicantPersonViewModel";
import { CSTermsAgreementDocumentPath, DETermsAgreementDocumentPath, ENTermsAgreementDocumentPath } from "../../../config";
import OutageService from "../../../services/outageService";
import OutagesViewModel from "../../common/outagesViewModel";
import RefundRequestService from "../../../services/refundRequestService";
import CustomerRefundApplicationReason from "../../../entities/customerRefundApplicationReason";
import CustomerRefundApplicationOrderSourceType from "../../../entities/customerRefundApplicationOrderSourceType";
import ChargeTypesService from "services/chargeTypesService";
import CustomerRefundApplicationProductionResponse from "entities/customerRefundApplicationProductionResponse";
import { NoticeTechnicalLicenseRules } from "models/refundRequestNotice";

export const RefundRequestNavigationName = "refund_request_notice";

@Router.registerRoute({ route: RefundRequestNavigationName, name: Router.Self })
export default class RefundRequestViewModel extends NoticeViewModel {
  @observable tooltipPosition: "right-start" | "bottom";
  @observable applicantPersonViewModel: ApplicantPersonViewModel;
  @observable vehicleInfoViewModel: VehicleInfoViewModel;
  @observable allowCreateExemption = false;

  constructor(
    public localizationService: LocalizationService,
    protected enumService: EnumService,
    protected chargeTypesService: ChargeTypesService,
    protected niaService: NiaService,
    protected isdsService: ISDSService,
    protected outageService: OutageService,
    protected eventBus: EventBus,
    protected service: RefundRequestService,
    protected router: AppRouter,
    protected outagesViewModelFactory: ReturnType<typeof OutagesViewModel.Factory>,
    private chargeRegistrationService: ChargeRegistrationService,
    private vehicleInfoVMFactory: ReturnType<typeof VehicleInfoViewModel.Factory>,
    private applicantPersonInputVMFactory: ReturnType<typeof ApplicantPersonViewModel.Factory>
  ) {
    super(
      localizationService,
      enumService,
      niaService,
      isdsService,
      outageService,
      eventBus,
      service,
      router,
      outagesViewModelFactory
    );
    this.navigationName = RefundRequestNavigationName;
    this.createChildren();
    window.addEventListener("resize", this.handleResize);
    this.setDefaultCountryId();
    attachManualValidator(this, true);
    this.attachValidators();

    this.unsubscriptions.push(
      this.eventBus.subscribe(refundRequestCreationFailed, this.generalOperationFailed),
      this.eventBus.subscribe(refundRequestDocumentsUploadFailed, this.isdsOperationFailed),
      this.eventBus.subscribe(refundRequestDocumentsUploadSuccess, this.isdsUploadOperationSuccess),
      this.eventBus.subscribe(refundRequestISDSConceptStatusCheckSuccess, this.isdsConceptStatusCheck),
      this.eventBus.subscribe(refundRequestISDSConceptStatusCheckFailure, this.isdsConceptFailure),
      this.eventBus.subscribe(refundRequestNIAResponseHandled, this.NiaResponseHandler),
      this.eventBus.subscribe(refundRequestISDSSessionIdHandled, this.ISDSSessionIdHandler),
      reaction(() => this.notice.reason, this.reasonChangeReaction),
      reaction(() => this.notice.chargeOrderSourceType, this.orderSourceTypeChangeReaction)
    );
  }

  @computed
  get newVehicleErrorMessage() {
    return getValidationMessage(this, "notice");
  }

  @computed
  get customerRefundApplicationProduction(): CustomerRefundApplicationProductionResponse | undefined {
    return this.service.customerRefundApplicationProduction;
  }

  @computed
  get hasToUploadPowerOfAttorney() {
    return false;
  }

  @computed
  get hasToUploadTechnicalLicense() {
    return this.notice._signatureType === SignatureType.DataBox;
  }

  @computed get notice() {
    return this.service.notice;
  }

  @bound
  showBirthdate() {
    return false;
  }

  @computed
  get isContinueDisabled(): boolean {
    return this.service.customerRefundApplicationProduction?.canBeAppliedFor === false;
  }

  /**
   * Reasons for requesting the refund for SelectBox
   */
  @computed get reasons() {
    return Object.values(CustomerRefundApplicationReason).map((value) => ({
      id: value,
      label: this.translateAttribute(`reasons.${value}`),
    }));
  }

  @bound
  translateGeneral(code: string, disableUnbreakableRules = false) {
    return this.localizationService.translateGeneral(`refundRequest.${code}`, undefined, disableUnbreakableRules);
  }

  @bound
  translateAttribute(code: string) {
    return this.localizationService.translateAttribute("refundRequest", code);
  }

  @computed get termsAgreementDocumentPath() {
    switch (this.localizationService.locale) {
      case "cs":
        return CSTermsAgreementDocumentPath;
      case "de":
        return DETermsAgreementDocumentPath;
      default:
        return ENTermsAgreementDocumentPath;
    }
  }

  @action orderSourceTypeChangeReaction = () => {
    // Reset bank account number when it's not required
    if (
      (this.notice.chargeOrderSourceType === CustomerRefundApplicationOrderSourceType.Eshop ||
        this.notice.chargeOrderSourceType === CustomerRefundApplicationOrderSourceType.Kiosk) &&
      this.notice.bankAccount
    ) {
      this.notice.bankAccount = undefined;
    }
  };

  @action reasonChangeReaction = () => {
    // Reset Other reason text when it's not required
    if (this.notice.reason !== CustomerRefundApplicationReason.Other && this.notice.otherReasonText) {
      this.notice.otherReasonText = undefined;
    }
  };

  protected onInitialize(): Promise<any> {
    this.chargeRegistrationService.vehicleChargingInstructions = undefined;
    this.service.customerRefundApplicationProduction = undefined;

    this.unsubscriptions.push(
      reaction(
        () => [this.notice.vehicle.licensePlate, this.notice.vehicle.countryId, this.notice.reason],
        () => {
          this.handleVehicleInfoChange();
        },
        {
          fireImmediately: true,
        }
      ),
      reaction(
        () => [this.notice.vehicle.countryId, this.notice.vehicle.licensePlate],
        () => {
          if (this.notice.vehicle.countryId) {
            this.notice.countryId = this.notice.vehicle.countryId;
          }
          if (this.notice.vehicle.licensePlate) {
            this.notice.licensePlate = this.notice.vehicle.licensePlate;
          }
        }
      ),
      reaction(() => [this.notice.licensePlate, this.notice.countryId], this.persistNotice)
    );
    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean) {
    if (isClosing) {
      this.unsubscriptions.forEach((unsubscribe) => unsubscribe());
    }
    window.removeEventListener("resize", this.handleResize);
    return super.onDeactivate(isClosing);
  }

  protected attachValidators() {
    attachAutomaticBlurValidator(this.notice, {
      ...NoticeTechnicalLicenseRules,
      ...this.notice.rules,
    });
  }

  handleResize = () => {
    this.tooltipPosition = getTooltipPosition(window);
  };

  private translateOrderItem(code: string) {
    return this.localizationService.translateAttribute("orderItem", code);
  }

  async navigate(subPath: string | undefined, params: any) {
    if (!params) return;

    if (params.sessionId) {
      if (sessionStorage.getItem(SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY)) {
        sessionStorage.removeItem(SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY);
        await this.isdsService.conceptStatus(params.sessionId, Notice.REFUND_REQUEST);
        return;
      }
      this.eventBus.publish(refundRequestISDSSessionIdHandled({ sessionId: params.sessionId }));
    } else if (params.response) {
      const response = params.response;

      if (response && this.notice && this.notice.applicant) {
        const urlDecoded = decodeURI(response);
        const base64Decoded = atob(urlDecoded);
        const niaResponse = plainToClass(NiaResponse, JSON.parse(base64Decoded));

        if (!niaResponse) return;

        this.eventBus.publish(refundRequestNIAResponseHandled({ niaResponse }));
      }
    }
  }

  @action.bound
  protected isdsConceptStatusCheck(data: { type: string; payload: { success: boolean; notice: Notice } }) {
    if (data.payload.success && data.payload.notice) {
      this.router.navigate("refund_request_documents", undefined, {
        notice: data.payload.notice,
      });
    }
  }

  @action.bound
  protected isdsUploadOperationSuccess(data: { type: string; payload: { conceptIdent: string } }) {
    super.isdsUploadOperationSuccess(data);
    this.isdsService.redirectToISDSConcept(Notice.REFUND_REQUEST, data.payload.conceptIdent);
  }

  @bound
  private async handleVehicleInfoChange() {
    this.allowCreateExemption = false;
    this.service.customerRefundApplicationProduction = undefined;
    this.chargeRegistrationService.vehicleChargingInstructions = undefined;
  }

  @bound
  translateWarningReason(code: string): string {
    if (this.service.customerRefundApplicationProduction?.chargeManagementUrl) {
      return this.localizationService
        .translateGeneral(`eshop.order.license.exemptionReasonWarnings.${code}`, undefined, false)
        .replace("%link%", this.service.customerRefundApplicationProduction?.chargeManagementUrl);
    }
    return this.localizationService.translateGeneral(`eshop.order.license.exemptionReasonWarnings.${code}`, undefined, false);
  }

  @bound
  protected async persistNotice() {
    sessionStorage.setItem(Notice.REFUND_REQUEST, serializeNotice(this.notice));

    await super.persistNotice();
  }

  @action.bound
  public async createNotice(): Promise<string | undefined> {
    this.errorMessage = undefined;
    const countryId = this.notice.vehicle.countryId;
    const licensePlate = this.notice.vehicle.licensePlate;

    if (!countryId || !licensePlate) return Promise.resolve(undefined);

    const response = (await this.service.getProduction(this.notice, countryId, licensePlate)) as any;

    if (response.errors) {
      this.creationLoadingModalToggle(false);
      this.errorMessage = this.localizationService.translateGeneral("errors.invalidData");
      return;
    }
    if (
      (this.customerRefundApplicationProduction?.warningReasons?.length ||
        this.customerRefundApplicationProduction?.rejectionReason ||
        this.customerRefundApplicationProduction?.canBeAppliedFor === false) &&
      !this.allowCreateExemption
    ) {
      this.allowCreateExemption = true;

      return Promise.resolve(undefined);
    }

    return this.service.createRefundRequest(this.notice, countryId, licensePlate);
  }

  @action.bound
  public uploadDocuments(ISDSSessionId: string, files: PersistedBase64[]): Promise<void> | undefined {
    this.errorMessage = undefined;

    return this.service.uploadDocuments(ISDSSessionId, files);
  }

  @action.bound
  public redirectToISDS(): void {
    this.errorMessage = undefined;
    this.creationLoadingModalToggle(false);
    this.isdsService.redirectToISDS(Notice.REFUND_REQUEST);
  }

  @action.bound
  public redirectToNia() {
    this.errorMessage = undefined;
    this.creationLoadingModalToggle(false);
    this.niaService.redirectToNia(Notice.REFUND_REQUEST);
  }

  protected createChildren() {
    this.children.clear();
    this.children.push(
      (this.vehicleInfoViewModel = this.vehicleInfoVMFactory(this.notice.vehicle!, {
        attachValidator: true,
        fixedLicensePlateSize: false,
        initByLocalization: true,
      })),
      (this.applicantPersonViewModel = this.applicantPersonInputVMFactory(this.notice.applicant, this.GDPR, this.showBirthdate()))
    );
  }
}
